import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import React, { useEffect, useRef, useState } from 'react';

import i18n from '../../../i18n';
import AppButton from '../buttons/AppButton';
import AppConfirmDialog from '../dialogs/AppConfirmDialog';
import Empty from '../Empty';
import Spinner from '../spinner';
import Toolbar from '../toolbar/Toolbar';
import ViewToggle from '../view_toggle/ViewToggle';

import FileEntry, { fileIcon } from './FileEntry';
import styles from './FileManager.module.scss';
import useFileManager from './useFileManager';
// Import diretto e non dal barrel `@ui`: essendo `FileManager` a sua volta
// esportato da `@ui`, passare dal barrel creerebbe un ciclo di dipendenze.
import Icon from '../icon/Icon';
import { ICONS } from '@/constants/icons';

/** Crumb del breadcrumb che è anche un target di drop per lo spostamento. */
function DropCrumb({ id, targetPrefix, onClick, children, last }) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { targetPrefix } });
  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`${styles.crumb} ${last ? styles.crumbLast : ''} ${isOver ? styles.dropActive : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const parentOf = (fullPath) => fullPath.split('/').slice(0, -1).join('/');

/**
 * File manager riusabile (cartelle + sottocartelle) su Firebase Storage.
 * Vista carte/lista, ricerca, ordinamento, upload drag-and-drop da desktop,
 * trascina-per-spostare (@dnd-kit) e rinomina. Le cartelle sono prefissi di
 * path; la navigazione è interna (breadcrumb), non sincronizzata con l'URL.
 *
 * @param {string} basePath prefisso radice (es. managers/{g}/fleet/vehicles/{id}/documents)
 * @param {boolean} [readOnly] nasconde le azioni di modifica
 * @param {boolean} [embedded] reso dentro una tab (senza wrapper di pagina)
 */
export default function FileManager({ basePath, readOnly = false, embedded = false }) {
  const fm = useFileManager(basePath);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const folderInputRef = useRef(null);
  const [pendingDelete, setPendingDelete] = useState(null); // { kind, fullPath, name }
  const [pendingMove, setPendingMove] = useState(null); // { item, targetPrefix }
  const [activeItem, setActiveItem] = useState(null); // item trascinato
  const [dropping, setDropping] = useState(false); // overlay upload desktop

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (newFolderOpen) folderInputRef.current?.focus();
  }, [newFolderOpen]);

  const submitNewFolder = async () => {
    await fm.addFolder(newFolderName);
    setNewFolderName('');
    setNewFolderOpen(false);
  };

  const handleUploadInput = (event) => {
    fm.upload(event.target.files);
    event.target.value = '';
  };

  const openItem = (item) => {
    if (item.kind === 'folder') fm.enterFolder(item.name);
    else window.open(item.url, '_blank', 'noopener,noreferrer');
  };

  const confirmDelete = async () => {
    const item = pendingDelete;
    setPendingDelete(null);
    if (!item) return;
    if (item.kind === 'folder') await fm.removeFolder(item.fullPath);
    else await fm.removeFile(item.fullPath);
  };

  // ===== DnD spostamento =====
  const onDragStart = (e) => setActiveItem(e.active.data.current?.item || null);
  const onDragEnd = (e) => {
    setActiveItem(null);
    const item = e.active.data.current?.item;
    const targetPrefix = e.over?.data.current?.targetPrefix;
    if (!item || !targetPrefix) return;
    if (targetPrefix === parentOf(item.fullPath)) return; // già lì
    if (item.kind === 'folder' && targetPrefix.startsWith(item.fullPath)) return; // dentro sé stessa
    setPendingMove({ item, targetPrefix });
  };
  const confirmMove = async () => {
    const mv = pendingMove;
    setPendingMove(null);
    if (mv) await fm.move(mv.item, mv.targetPrefix);
  };

  // ===== Upload drag-and-drop da desktop (HTML5, distinto dal DnD @dnd-kit) =====
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const onNativeDragOver = (e) => {
    if (!readOnly && hasFiles(e)) {
      e.preventDefault();
      setDropping(true);
    }
  };
  const onNativeDrop = (e) => {
    if (readOnly || !hasFiles(e)) return;
    e.preventDefault();
    setDropping(false);
    fm.upload(e.dataTransfer.files);
  };

  const folderItems = fm.folders.map((f) => ({ ...f, kind: 'folder' }));
  const fileItems = fm.files.map((f) => ({ ...f, kind: 'file' }));
  const allItems = [...folderItems, ...fileItems];
  const isEmpty = !fm.loading && allItems.length === 0;

  const sortSelect = (
    <select
      className={styles.sortSelect}
      value={fm.sortKey}
      onChange={(e) => fm.setSortKey(e.target.value)}
      aria-label={i18n.t('ordina', { defaultValue: 'Ordina' })}
    >
      <option value="name">{i18n.t('nome', { defaultValue: 'Nome' })}</option>
      <option value="updated">{i18n.t('modificato', { defaultValue: 'Modificato' })}</option>
      <option value="size">{i18n.t('dimensione', { defaultValue: 'Dimensione' })}</option>
    </select>
  );

  const newFolderControl = newFolderOpen ? (
    <div className={styles.newFolder}>
      <input
        ref={folderInputRef}
        type="text"
        className={styles.folderInput}
        placeholder={i18n.t('nome_cartella', { defaultValue: 'Nome cartella' })}
        value={newFolderName}
        onChange={(e) => setNewFolderName(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && submitNewFolder()}
      />
      <AppButton
        mode="icon"
        variant="primary"
        size="sm"
        icon={ICONS.confirm}
        title={i18n.t('conferma', { defaultValue: 'Conferma' })}
        disabled={!newFolderName.trim()}
        onClick={submitNewFolder}
      />
      <AppButton
        mode="icon"
        variant="neutral"
        size="sm"
        icon={ICONS.close}
        title={i18n.t('annulla')}
        onClick={() => {
          setNewFolderOpen(false);
          setNewFolderName('');
        }}
      />
    </div>
  ) : (
    <AppButton
      mode="action"
      variant="neutral"
      icon={ICONS.addFolder}
      onClick={() => setNewFolderOpen(true)}
    >
      {i18n.t('nuova_cartella', { defaultValue: 'Nuova cartella' })}
    </AppButton>
  );

  const body = (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className={styles.fileManager} aria-busy={fm.loading || fm.busy}>
        {/* Breadcrumb (ogni crumb è anche drop target) */}
        <nav className={styles.breadcrumb} aria-label="breadcrumb">
          <DropCrumb
            id="crumb:0"
            targetPrefix={basePath}
            onClick={() => fm.goToIndex(0)}
            last={!fm.path.length}
          >
            <Icon name="folderOpen" /> {i18n.t('documenti')}
          </DropCrumb>
          {fm.path.map((seg, i) => (
            <React.Fragment key={`${seg}-${i}`}>
              <i className={`fa-solid fa-angle-right ${styles.sep}`} />
              <DropCrumb
                id={`crumb:${i + 1}`}
                targetPrefix={[basePath, ...fm.path.slice(0, i + 1)].join('/')}
                onClick={() => fm.goToIndex(i + 1)}
                last={i === fm.path.length - 1}
              >
                {seg}
              </DropCrumb>
            </React.Fragment>
          ))}
        </nav>

        {/* Toolbar: nuova cartella + upload | ricerca | sort + vista */}
        <Toolbar
          filters={
            !readOnly ? (
              <div className={styles.toolbarFilters}>
                {newFolderControl}
                <label className={styles.uploadBtn}>
                  <Icon name="upload" /> {i18n.t('carica')}
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleUploadInput}
                    disabled={fm.busy}
                  />
                </label>
              </div>
            ) : null
          }
          searchValue={fm.query}
          onSearchChange={fm.setQuery}
          searchPlaceholder={i18n.t('cerca')}
          actions={
            <div className={styles.toolbarActions}>
              <ViewToggle value={fm.viewMode} onChange={fm.setViewMode} />
            </div>
          }
        />

        {/* Area contenuti = drop-zone upload desktop */}
        <div
          className={`${styles.dropzone} ${dropping ? styles.droppingActive : ''}`}
          onDragOver={onNativeDragOver}
          onDragLeave={() => setDropping(false)}
          onDrop={onNativeDrop}
        >
          {fm.viewMode === 'cards' ? (
            <div className={styles.grid}>
              {allItems.map((item) => (
                <FileEntry
                  key={item.fullPath}
                  item={item}
                  viewMode="cards"
                  readOnly={readOnly}
                  onOpen={openItem}
                  onRename={fm.rename}
                  onDelete={setPendingDelete}
                />
              ))}
            </div>
          ) : (
            <div className={styles.list}>
              <div className={styles.listHead}>
                <span>{i18n.t('nome', { defaultValue: 'Nome' })}</span>
                <span className={styles.colSize}>
                  {i18n.t('dimensione', { defaultValue: 'Dimensione' })}
                </span>
                <span className={styles.colDate}>
                  {i18n.t('modificato', { defaultValue: 'Modificato' })}
                </span>
                <span />
              </div>
              {allItems.map((item) => (
                <FileEntry
                  key={item.fullPath}
                  item={item}
                  viewMode="table"
                  readOnly={readOnly}
                  onOpen={openItem}
                  onRename={fm.rename}
                  onDelete={setPendingDelete}
                />
              ))}
            </div>
          )}

          {isEmpty && (
            <Empty
              icon={ICONS.folderOpen}
              title={i18n.t('nessun_documento', { defaultValue: 'Nessun documento' })}
            />
          )}

          {dropping && (
            <div className={styles.dropOverlay}>
              <Icon name="cloudUpload" />
              <span>
                {i18n.t('rilascia_per_caricare', { defaultValue: 'Rilascia per caricare' })}
              </span>
            </div>
          )}

          {/* Lo spostamento avviene lato server: qui basta segnalare l'attesa. */}
          {fm.busy && (
            <div className={styles.dropOverlay}>
              <Icon name="loading" />
              <span>{i18n.t('operazione_in_corso', { defaultValue: 'Operazione in corso' })}</span>
            </div>
          )}
        </div>
      </div>

      <DragOverlay>
        {activeItem ? (
          <div className={styles.dragGhost}>
            <i
              className={`fa-solid ${activeItem.kind === 'folder' ? 'fa-folder' : fileIcon(activeItem.name)}`}
            />
            <span>{activeItem.name}</span>
          </div>
        ) : null}
      </DragOverlay>

      <AppConfirmDialog
        open={!!pendingDelete}
        title={i18n.t('elimina')}
        message={
          pendingDelete?.kind === 'folder'
            ? i18n.t('sicuro_rimuovi_cartella', {
                defaultValue: 'Eliminare la cartella e tutto il suo contenuto?',
              })
            : i18n.t('sicuro_rimuovi')
        }
        confirmLabel={i18n.t('elimina')}
        cancelLabel={i18n.t('annulla')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <AppConfirmDialog
        open={!!pendingMove}
        variant="primary"
        icon={ICONS.move}
        title={i18n.t('sposta', { defaultValue: 'Sposta' })}
        message={i18n.t('sicuro_sposta', {
          defaultValue: 'Spostare "{{name}}" qui?',
          name: pendingMove?.item?.name,
        })}
        confirmLabel={i18n.t('sposta', { defaultValue: 'Sposta' })}
        cancelLabel={i18n.t('annulla')}
        onConfirm={confirmMove}
        onCancel={() => setPendingMove(null)}
      />
    </DndContext>
  );

  if (embedded) return body;

  return (
    <div id="file-manager">
      <Spinner visible={fm.loading} />
      <div className="content-wrapper">
        <div className="content pt-3">{body}</div>
      </div>
    </div>
  );
}
